import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getDb, getRealtimeManager } from '@talk2nick/shared';
import type { User } from 'firebase/auth';
import type { PinSetting } from '@talk2nick/core';
import { verifyPin as verifyPinLocal } from '@/pin';

/** M1 설계 — PLAN 5-12장 */
export interface LockState {
  isLocked: boolean;
  remainingAttempts: number;
  /** 5회 실패로 걸린 잠금의 만료 시각(ms). 수동/재실행 잠금은 null */
  lockUntil: number | null;
  tryUnlock: (pin: string) => Promise<void>;
  lock: () => void;
  clearLock: () => void;
}

/**
 * 잠금은 Firebase 세션과 별개인 이 기기의 로컬 게이트다.
 * 새로고침으로 시도 횟수가 초기화되지 않도록 `localStorage`에 영속한다
 * (브루트포스 억제 — PLAN 5-12장).
 */
const STORAGE_KEY_PREFIX = 'talk2nick_lock_store.';

/**
 * 잠금 상태는 계정별로 분리한다.
 *
 * 한 키를 계정 구분 없이 쓰면, 한 계정에서 잠근 상태가 같은 브라우저의 다른
 * 계정에도 그대로 적용된다. 계정마다 PIN이 다르므로 그 계정은 자기 PIN을
 * 입력해도 풀 수 없고, 프로필이 없는 계정에서는 아예 잠금 해제 경로가 없다.
 * 따라서 uid를 키에 포함해 계정 간에 넘어가지 않게 한다.
 *
 * uid는 비동기로 확정되므로 모듈 로드 시점에는 알 수 없다. 키 접미사는
 * setActiveUser()가 uid를 전달할 때 채워지고, 그 전까지는 메모리 상태로만 쓴다.
 */
let activeUid: string | null = null;

function storageKey(uid: string | null): string | null {
  return uid ? `${STORAGE_KEY_PREFIX}${uid}` : null;
}

/** 이전 구현의 잔여 키 — 계정별 키로 옮기면서 정리한다 */
const LEGACY_KEYS = ['talk2nick_lock_store', 'talk2nick_locked', 'talk2nick_unlockedAt'];

const MAX_ATTEMPTS = 5;
/** 5회 실패 → 1시간 잠금 */
const LOCK_DURATION_MS = 60 * 60 * 1000;
/** 앱 로드 시 마지막 해제로부터 30분 초과 → 잠금 (PLAN 변경 3) */
const INACTIVITY_LOCK_MS = 30 * 60 * 1000;
/** 백그라운드 5분 초과 → 잠금 (PLAN 변경 3) */
const BACKGROUND_LOCK_MS = 5 * 60 * 1000;

export interface LockSnapshot {
  isLocked: boolean;
  remainingAttempts: number;
  lockUntil: number | null;
  /** 마지막 잠금 해제 시각(ms) — 잠금 조건 판정 기준 */
  unlockedAt: number | null;
}

const FRESH: LockSnapshot = {
  isLocked: false,
  remainingAttempts: MAX_ATTEMPTS,
  lockUntil: null,
  unlockedAt: null,
};

const listeners = new Set<() => void>();
/** 모듈 로드 시 1회 복원 — 컴포넌트는 이 참조를 비교하므로 불변 객체로 교체한다. */
let snapshot: LockSnapshot = hydrate();

function getSnapshot(): LockSnapshot {
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function commit(next: LockSnapshot): void {
  snapshot = next;
  persist(next);
  for (const listener of listeners) listener();
}

function persist(state: LockSnapshot): void {
  if (typeof window === 'undefined') return;
  const key = storageKey(activeUid);
  // uid가 아직 없으면 쓰지 않는다 — 계정 구분 없는 키로 새면 다음 로그인 계정이
  // 이 상태를 물려받는다. 메모리 상태로는 유지되므로 잠금 기능 자체는 동작한다.
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    // 사파리 프라이빗 모드 등 저장 실패 — 잠금은 메모리 상태로만 유지된다
    console.error('[talk2nIcK] 잠금 상태 저장 실패:', error);
  }
}

/**
 * 로그인한 계정을 알려준다. 계정이 바뀌면 그 계정의 잠금 상태로 교체한다.
 *
 * 잠금 상태는 계정 간에 넘어가면 안 되므로, 여기서 키를 갈아 끼운다.
 * 이미 잠겨 있으면 새 계정의 상태를 먼저 읽고, 없으면 잠금 없는 상태로 시작한다.
 */
export function setActiveUid(uid: string | null): void {
  if (activeUid === uid) return;
  activeUid = uid;
  snapshot = hydrate();
  for (const listener of listeners) listener();
}

/**
 * 저장된 상태 복원.
 * 만료된 5회 실패 잠금은 시도 횟수와 함께 초기화하고, 마지막 해제로부터
 * 30분이 지났거나 잠금 플래그가 남아 있으면 잠금 상태로 시작한다.
 */
function hydrate(): LockSnapshot {
  if (typeof window === 'undefined') return FRESH;

  let raw: string | null = null;
  try {
    for (const key of LEGACY_KEYS) window.localStorage.removeItem(key);
    const key = storageKey(activeUid);
    // uid가 없으면 읽을 계정 키가 없다 — 잠금 없는 상태로 시작한다
    raw = key ? window.localStorage.getItem(key) : null;
  } catch {
    // 저장소를 쓸 수 없는 환경(프라이빗 모드 등) — 잠금 없이 진행한다
    return FRESH;
  }
  if (!raw) return FRESH;

  let parsed: Partial<LockSnapshot>;
  try {
    parsed = JSON.parse(raw) as Partial<LockSnapshot>;
  } catch {
    return FRESH;
  }

  const now = Date.now();
  let remainingAttempts =
    typeof parsed.remainingAttempts === 'number'
      ? Math.min(Math.max(Math.trunc(parsed.remainingAttempts), 0), MAX_ATTEMPTS)
      : MAX_ATTEMPTS;

  let lockUntil = typeof parsed.lockUntil === 'number' ? parsed.lockUntil : null;
  if (lockUntil !== null && lockUntil <= now) {
    // 1시간이 지났다 → 다시 시도할 수 있게 횟수를 되돌린다
    lockUntil = null;
    remainingAttempts = MAX_ATTEMPTS;
  }

  const lockedOut = lockUntil !== null;
  const unlockedAt = typeof parsed.unlockedAt === 'number' ? parsed.unlockedAt : null;
  const stale = unlockedAt !== null && now - unlockedAt > INACTIVITY_LOCK_MS;
  const isLocked = lockedOut || parsed.isLocked === true || stale;

  return { isLocked, remainingAttempts, lockUntil, unlockedAt: isLocked ? null : unlockedAt };
}

/**
 * PIN 검증 후 잠금을 해제한다.
 *
 * 실패하면 남은 횟수를 실제로 하나 줄이고, 0이 되면 1시간 잠금을 건다
 * (영속하므로 새로고침으로 우회할 수 없다). 메시지 문자열은 잠금 화면이
 * i18n 키로 매핑하므로 계약이다.
 *
 * 검증에는 `users/{uid}` 읽기가 필요하므로 잠금 중이라도 Firestore를 잠깐 쓰는데,
 * 그때 다시 채워진 로컬 캐시를 남기지 않도록 실패 경로에서 즉시 비운다.
 */
async function verifyAndUnlock(pin: string, user: User | null): Promise<void> {
  const current = getSnapshot();
  const now = Date.now();

  // 5회 실패 잠금이 아직 살아 있으면 PIN 검증 자체를 하지 않는다
  if (current.lockUntil !== null && current.lockUntil > now) {
    throw new Error('status.5attempts');
  }
  if (!current.isLocked) return;
  if (!user) throw new Error('status.noAuth');

  const snap = await getDoc(doc(getDb(), 'users', user.uid));
  if (!snap.exists()) throw new Error('status.noProfile');

  const pinSetting = (snap.data() as { settings?: { pin?: PinSetting } }).settings?.pin;
  const ok = pinSetting !== undefined && (await verifyPinLocal(pin, pinSetting));

  if (ok) {
    commit({
      isLocked: false,
      remainingAttempts: MAX_ATTEMPTS,
      lockUntil: null,
      unlockedAt: Date.now(),
    });
    return;
  }

  // 방금 읽은 프로필이 디스크 캐시로 굳지 않도록 다시 비운다 (성공 경로는 곧 구독을 붙이므로 유지)
  await purgeLockedCache();

  const remaining = Math.max(getSnapshot().remainingAttempts - 1, 0);
  if (remaining === 0) {
    commit({
      isLocked: true,
      remainingAttempts: 0,
      lockUntil: Date.now() + LOCK_DURATION_MS,
      unlockedAt: null,
    });
    throw new Error('status.5attempts');
  }

  commit({ isLocked: true, remainingAttempts: remaining, lockUntil: null, unlockedAt: null });
  throw new Error('status.wrongPin');
}

/** 수동 잠금 / 백그라운드 초과 잠금 — 5회 실패 잠금은 덮어쓰지 않는다 */
function lockNow(): void {
  const current = getSnapshot();
  const lockedOut = current.lockUntil !== null && current.lockUntil > Date.now();
  commit({
    isLocked: true,
    remainingAttempts: lockedOut ? current.remainingAttempts : MAX_ATTEMPTS,
    lockUntil: lockedOut ? current.lockUntil : null,
    unlockedAt: null,
  });
}

/** 로그아웃·계정 전환처럼 잠금 자체를 없애야 할 때 */
function clearAll(): void {
  commit({ ...FRESH });
}

/**
 * 잠금이 풀린 상태에서 마지막 활동 시각을 기록한다.
 * 잠금 중에는 아무것도 하지 않는다 (잠금 화면을 띄운 채로 유지).
 */
function markUnlocked(): void {
  const current = getSnapshot();
  if (current.isLocked) return;
  commit({ ...current, unlockedAt: Date.now() });
}

/**
 * 앱 로드 / 백그라운드 초과 잠금 감시 (PLAN 변경 3).
 * `LockGate`에서 한 번만 사용한다 — 감시자가 둘이면 잠금 판정이 중복된다.
 */
export function useLockWatcher(enabled: boolean, isLocked: boolean): void {
  useEffect(() => {
    // PIN이 없는 사용자는 잠글 대상이 아니고, 이미 잠겼으면 감시할 필요가 없다
    if (!enabled || isLocked) return;

    markUnlocked();

    let hiddenAt: number | null = null;

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null && Date.now() - hiddenAt > BACKGROUND_LOCK_MS) {
        lockNow();
      } else {
        markUnlocked();
      }
      hiddenAt = null;
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, isLocked]);
}

/**
 * 잠금 진입 시 활성 네트워크 리스너 정리 (PLAN v0.2 Phase 5).
 *
 * UI 인터랙션은 LockGate로 차단하고 활성 구독/네트워크 리스너는 teardown()으로 분리한다.
 * IndexedDB(persistentLocalCache)는 보존하여 오프라인 PWA 복원력과
 * PIN 잠금 해제 시 0ms 즉시 화면 렌더 및 증분 동기화를 보장한다.
 */
export async function purgeLockedCache(): Promise<void> {
  getRealtimeManager().teardown();
}

/**
 * uid 없이 잠금 여부만 필요한 소비처용 (useAuth가 프로필 구독을 끊을지 판단).
 * `LockSnapshot`은 불변 객체로 교체되므로 참조 비교가 성립한다.
 */
export function useLockSnapshot(): LockSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useLockStore(user: User | null): LockState {
  const state = useLockSnapshot();

  const tryUnlock = useCallback((pin: string) => verifyAndUnlock(pin, user), [user]);
  const lock = useCallback(lockNow, []);
  const clearLock = useCallback(clearAll, []);

  return {
    isLocked: state.isLocked,
    remainingAttempts: state.remainingAttempts,
    lockUntil: state.lockUntil,
    tryUnlock,
    lock,
    clearLock,
  };
}
